import React, { useState, useEffect } from 'react';
import './ContactModal.css';
import axios from 'axios';
import config from '../../config';

const ContactModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        description: ''
    });
    const [files, setFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [errors, setErrors] = useState({});
    const [recaptchaKey, setRecaptchaKey] = useState('');

    // Load reCAPTCHA key and script
    useEffect(() => {
        const loadRecaptchaKey = async () => {
            try {
                const response = await axios.get(`${config.baseURL}api/landing_page_api.php/contact/recaptcha-keys`);
                const siteKey = response.data.site_key;
                setRecaptchaKey(siteKey);

                if (!document.getElementById('recaptcha-script')) {
                    const script = document.createElement('script');
                    script.id = 'recaptcha-script';
                    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
                    script.async = true;
                    script.defer = true;
                    document.body.appendChild(script);
                }
            } catch (error) {
                console.error('Failed to load reCAPTCHA key:', error);
            }
        };

        loadRecaptchaKey();

        return () => {
            const recaptchaScript = document.getElementById('recaptcha-script');
            if (recaptchaScript) {
                recaptchaScript.remove();
            }
            const grecaptchaBadge = document.querySelector('.grecaptcha-badge');
            if (grecaptchaBadge) {
                grecaptchaBadge.remove();
            }
        };
    }, []);

    const executeRecaptcha = async () => {
        if (!window.grecaptcha || !recaptchaKey) {
            console.warn('reCAPTCHA is not ready yet, retrying in 1 second...');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return executeRecaptcha();
        }

        try {
            const token = await window.grecaptcha.execute(recaptchaKey, { action: 'contact' });
            return token;
        } catch (error) {
            console.error('reCAPTCHA execution failed:', error);
            return null;
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.subject) {
            newErrors.subject = 'Subject is required';
        }

        if (!formData.description) {
            newErrors.description = 'Description is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));

        // Clear error for this field when user types
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            setFiles(prevFiles => {
                // Limit to max 5 files
                const combinedFiles = [...prevFiles, ...newFiles].slice(0, 5);
                return combinedFiles;
            });
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFiles = Array.from(e.dataTransfer.files);
            setFiles(prevFiles => {
                // Limit to max 5 files
                const combinedFiles = [...prevFiles, ...droppedFiles].slice(0, 5);
                return combinedFiles;
            });
        }
    };

    const removeFile = (index) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setIsSubmitting(true);
        setSubmitResult(null);

        // Validate form
        const isValid = validateForm();
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }

        try {
            // Generate reCAPTCHA token
            const token = await executeRecaptcha();

            if (!token) {
                setErrors((prev) => ({
                    ...prev,
                    recaptcha: 'reCAPTCHA verification failed. Please refresh and try again.',
                }));
                setIsSubmitting(false);
                return;
            }

            // Create FormData object
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('subject', formData.subject);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('recaptcha_token', token);

            // Add files
            files.forEach((file) => {
                formDataToSend.append('files', file);
            });

            // Send request
            const response = await axios.post(`${config.baseURL}api/landing_page_api.php/contact/submit`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSubmitResult({
                success: true,
                message: response.data.message || 'Your message has been sent successfully!',
            });

            // Reset form fields
            setFormData({
                name: '',
                email: '',
                subject: '',
                description: '',
            });
            setFiles([]);

            // Close modal after a delay
            setTimeout(() => {
                onClose();
            }, 3000);

        } catch (error) {
            console.error('Form submission error:', error.response?.data || error.message);
            setSubmitResult({
                success: false,
                message: error.response?.data?.error || 'Failed to send message. Please try again later.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && onClose()}>
            <div className="contact-modal">
                {/* Header */}
                <div className="modal-header">
                    <h1>Send us a message</h1>
                    <button className="close-button" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                <div className="form-container">
                    <h2>Contact us</h2>

                    {submitResult && (
                        <div className={`submit-result ${submitResult.success ? 'success' : 'error'}`}>
                            {submitResult.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">Your name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">
                                Email <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                disabled={isSubmitting}
                                className={errors.email ? 'error' : ''}
                            />
                            {errors.email && <div className="error-message">{errors.email}</div>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="subject">
                                Subject <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                                disabled={isSubmitting}
                                className={errors.subject ? 'error' : ''}
                            />
                            {errors.subject && <div className="error-message">{errors.subject}</div>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">
                                Description <span className="required">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="6"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                disabled={isSubmitting}
                                className={errors.description ? 'error' : ''}
                            ></textarea>
                            {errors.description && <div className="error-message">{errors.description}</div>}
                        </div>

                        <div
                            className="file-upload-area"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <input
                                type="file"
                                id="file-upload"
                                multiple
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="file-upload" className="file-upload-label">
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 6.667v18.666M6.667 16h18.666" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <h5>Upload files (max 5)</h5>
                                <p>Click to add or drag & drop files.</p>
                            </label>

                            {files.length > 0 && (
                                <div className="uploaded-files">
                                    {files.map((file, index) => (
                                        <div key={index} className="file-item">
                                            <span className="file-name">{file.name}</span>
                                            <button
                                                type="button"
                                                className="remove-file"
                                                onClick={() => removeFile(index)}
                                                disabled={isSubmitting}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* reCAPTCHA badge */}
                        <div className="recaptcha-badge">
                            Protected by reCAPTCHA
                            {errors.recaptcha && <div className="error-message">{errors.recaptcha}</div>}
                        </div>

                        {/* This div will hold the hidden reCAPTCHA widget */}
                        <div id="recaptcha-container"></div>

                        <button type="submit" className="submit-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactModal;
